/*
  File explorer for the Dashboard.

  Layout (redesigned for mira04):
    - Three top-level tabs: Experiments | Snapshots | Analyses.
    - Each tab opens to a flat, searchable list (no month/day drill-down).
    - Drilling into an experiment shows its samples; into a sample, its images.
    - Friendly names everywhere; raw S3 strings still flow through the
      onClickSample / onClickOpenExperiment / onClickSnapshot / handleImageSelection
      callbacks unchanged so downstream pages keep working.
*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as React from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
  Snackbar,
  Alert,
  Box,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Typography,
  Skeleton,
  Button,
} from '@mui/material';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import FileList from './FileItem';
import { getS3Client, getDynamoDBClient } from '../utils/awsClients';
import { formatExperimentName, formatSampleName, formatImageName, compareByStampDesc } from '../utils/formatNames';

// AWS clients initialized lazily
let s3 = null;
let dynamoDB = null;
async function ensureClients() {
  if (!s3) s3 = await getS3Client();
  if (!dynamoDB) dynamoDB = await getDynamoDBClient();
}

const TABS = ['experiments', 'snapshots', 'analyses'];

// View states:
//   experiments | samples | images          (Experiments tab)
//   snapshots | snapshot_images             (Snapshots tab)
//   analyses                                (Analyses tab)
function FileExplorer(props) {
  const [activeTab, setActiveTab] = useState('experiments');
  const [view, setView] = useState('experiments');
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');

  const [selectedExperiment, setSelectedExperiment] = useState(null); // raw id
  const [selectedSample, setSelectedSample] = useState(null); // raw id
  const [selectedSnapshot, setSelectedSnapshot] = useState(null); // raw id

  const [isLoading, setIsLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [movieDownloading, setMovieDownloading] = useState(false);

  // Image pagination
  const [nextToken, setNextToken] = useState(null);
  const [prevTokens, setPrevTokens] = useState([]);
  const [forwardTokens, setForwardTokens] = useState([]);

  const cache = useRef({});

  // ---------- fetchers ----------

  const fetchExperiments = useCallback(async () => {
    await ensureClients();
    const cacheKey = `experiments-${props.device.device_bucket}`;
    if (cache.current[cacheKey]) {
      setData(cache.current[cacheKey]);
      return;
    }
    setIsLoading(true);
    try {
      const result = await dynamoDB.query({
        TableName: 'Experiment_folders',
        KeyConditionExpression: '#bucket = :bucket',
        ExpressionAttributeNames: { '#bucket': 'USER_BUCKET', '#exp': 'experiment_name' },
        ExpressionAttributeValues: { ':bucket': props.device.device_bucket },
        FilterExpression: 'attribute_exists(#exp)',
        ProjectionExpression: '#exp',
      }).promise();
      const seen = new Set();
      const experiments = [];
      for (const item of (result.Items || [])) {
        const name = item.experiment_name;
        if (typeof name !== 'string' || !name || seen.has(name)) continue;
        // Hide snapshot folders from the Experiments tab (they have their own tab).
        if (name.endsWith('_Snapshots')) continue;
        seen.add(name);
        experiments.push({ raw: name, ...formatExperimentName(name) });
      }
      experiments.sort((a, b) => compareByStampDesc(a.raw, b.raw));
      cache.current[cacheKey] = experiments;
      setData(experiments);
    } catch (err) {
      console.error('Failed to fetch experiments:', err);
      setAlertMessage('Failed to load experiments.');
      setAlertSeverity('error');
      setAlertOpen(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [props.device]);

  const fetchSamples = useCallback(async (experimentRaw) => {
    await ensureClients();
    const cacheKey = `samples-${props.device.device_bucket}-${experimentRaw}`;
    if (cache.current[cacheKey]) {
      setData(cache.current[cacheKey]);
      return;
    }
    setIsLoading(true);
    try {
      // Schema: each (USER_BUCKET, experiment_name) row holds sample_name as a List.
      const result = await dynamoDB.query({
        TableName: 'Experiment_folders',
        KeyConditionExpression: '#bucket = :bucket',
        ExpressionAttributeNames: { '#bucket': 'USER_BUCKET', '#exp': 'experiment_name' },
        ExpressionAttributeValues: { ':bucket': props.device.device_bucket, ':exp': experimentRaw },
        FilterExpression: '#exp = :exp',
        ProjectionExpression: 'sample_name',
      }).promise();
      const seen = new Set();
      const samples = [];
      for (const item of (result.Items || [])) {
        const list = Array.isArray(item.sample_name) ? item.sample_name : [item.sample_name];
        for (const name of list) {
          if (typeof name !== 'string' || !name || seen.has(name)) continue;
          seen.add(name);
          samples.push(name);
        }
      }
      // Sort by leading slot number when present, else alphabetical.
      const slotOf = (s) => {
        const m = s.match(/^(\d+)-/);
        return m ? parseInt(m[1], 10) : 0;
      };
      samples.sort((a, b) => {
        const diff = slotOf(a) - slotOf(b);
        return diff !== 0 ? diff : a.localeCompare(b);
      });
      cache.current[cacheKey] = samples;
      setData(samples);
    } catch (err) {
      console.error('Failed to fetch samples:', err);
      setAlertMessage('Failed to load samples.');
      setAlertSeverity('error');
      setAlertOpen(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [props.device]);

  const fetchSnapshots = useCallback(async () => {
    await ensureClients();
    const cacheKey = `snapshots-${props.device.device_bucket}`;
    if (cache.current[cacheKey]) {
      setData(cache.current[cacheKey]);
      return;
    }
    setIsLoading(true);
    try {
      const prefix = `${props.device.device_bucket}/Experiments/`;
      const result = await s3.listObjectsV2({
        Bucket: process.env.REACT_APP_USER_BUCKET,
        Prefix: prefix,
        Delimiter: '/',
      }).promise();
      const folders = (result.CommonPrefixes || [])
        .map((p) => p.Prefix.replace(prefix, '').replace(/\/$/, ''))
        .filter((name) => name.endsWith('_Snapshots'))
        .map((name) => ({ raw: name, ...formatExperimentName(name) }));
      folders.sort((a, b) => compareByStampDesc(a.raw, b.raw));
      cache.current[cacheKey] = folders;
      setData(folders);
    } catch (err) {
      console.error('Failed to fetch snapshots:', err);
      setAlertMessage('Failed to load snapshots.');
      setAlertSeverity('error');
      setAlertOpen(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [props.device]);

  const fetchAnalyses = useCallback(async () => {
    await ensureClients();
    const cacheKey = `analyses-${props.device.device_bucket}`;
    if (cache.current[cacheKey]) {
      setData(cache.current[cacheKey]);
      return;
    }
    setIsLoading(true);
    try {
      const result = await dynamoDB.query({
        TableName: 'Analysis_folders',
        KeyConditionExpression: '#db = :db',
        FilterExpression: '(attribute_not_exists(#status) OR #status <> :deleted)',
        ExpressionAttributeNames: { '#db': 'device_bucket', '#status': 'status' },
        ExpressionAttributeValues: { ':db': props.device.device_bucket, ':deleted': 'deleted' },
        ScanIndexForward: false,
      }).promise();
      const items = (result.Items || []).map((item) => ({
        name: item.analysis_id,
        analysis_name: item.analysis_name || item.analysis_id,
        created_at: item.created_at,
        updated_at: item.updated_at,
        frame_count: item.frame_count || 0,
        measurement_count: item.measurement_count || 0,
        object_count: item.object_count || 0,
        s3_location: item.s3_location,
        source_path: item.source_path,
        status: item.status,
        mode: item.mode || 'standard',
        session_id: item.session_id || '',
      })).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      cache.current[cacheKey] = items;
      setData(items);
    } catch (err) {
      console.error('Failed to fetch analyses:', err);
      setAlertMessage('Failed to load saved analyses.');
      setAlertSeverity('error');
      setAlertOpen(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [props.device]);

  const fetchImages = useCallback(async (folder, token = null, direction = 'next', isSnapshot = false) => {
    await ensureClients();
    setIsLoading(true);
    try {
      const basePath = isSnapshot
        ? `${props.device.device_bucket}/Experiments/${folder}/`
        : `${props.device.device_bucket}/Experiments/${selectedExperiment}/${folder}/`;
      const result = await s3.listObjectsV2({
        Bucket: process.env.REACT_APP_USER_BUCKET,
        Prefix: basePath,
        MaxKeys: 20,
        ContinuationToken: token || undefined,
      }).promise();
      const prefixParts = basePath.split('/').filter(Boolean);
      const images = (result.Contents || [])
        .filter((obj) =>
          obj.Key.toLowerCase().endsWith('.jpg') &&
          obj.Key.split('/').filter(Boolean).length === prefixParts.length + 1)
        .map((obj) => obj.Key.split('/').pop());

      setData(images);
      if (images.length > 0) {
        props.handleImageSelection(images[images.length - 1]);
      }

      const newNextToken = result.IsTruncated ? result.NextContinuationToken : null;
      if (direction === 'next') {
        setPrevTokens((prev) => [...prev, token]);
        if (forwardTokens.length > 0) setForwardTokens((prev) => prev.slice(0, -1));
        setNextToken(newNextToken);
      } else if (direction === 'prev') {
        const currentToken = prevTokens[prevTokens.length - 1];
        if (currentToken !== undefined) {
          setForwardTokens((prev) => [...prev, currentToken]);
        }
        setPrevTokens((prev) => prev.slice(0, -1));
        setNextToken(newNextToken);
      } else {
        setPrevTokens([]);
        setForwardTokens([]);
        setNextToken(newNextToken);
      }
    } catch (err) {
      console.error('S3 fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [props, selectedExperiment, prevTokens, forwardTokens]);

  // ---------- per-tab loaders ----------

  const loadTab = useCallback((tab) => {
    setSearch('');
    if (tab === 'experiments') {
      setView('experiments');
      setSelectedExperiment(null);
      setSelectedSample(null);
      fetchExperiments();
    } else if (tab === 'snapshots') {
      setView('snapshots');
      setSelectedSnapshot(null);
      fetchSnapshots();
    } else if (tab === 'analyses') {
      setView('analyses');
      fetchAnalyses();
    }
  }, [fetchExperiments, fetchSnapshots, fetchAnalyses]);

  // initial / device-change
  useEffect(() => {
    if (!props.device?.device_bucket) return;
    cache.current = {};
    setActiveTab('experiments');
    loadTab('experiments');
  }, [props.device, loadTab]);

  // ---------- handlers ----------

  const handleTabChange = (_e, newTab) => {
    if (!TABS.includes(newTab) || newTab === activeTab) return;
    setActiveTab(newTab);
    loadTab(newTab);
  };

  const handleRefresh = () => {
    if (view === 'experiments') {
      delete cache.current[`experiments-${props.device.device_bucket}`];
      fetchExperiments();
    } else if (view === 'samples' && selectedExperiment) {
      delete cache.current[`samples-${props.device.device_bucket}-${selectedExperiment}`];
      fetchSamples(selectedExperiment);
    } else if (view === 'snapshots') {
      delete cache.current[`snapshots-${props.device.device_bucket}`];
      fetchSnapshots();
    } else if (view === 'analyses') {
      delete cache.current[`analyses-${props.device.device_bucket}`];
      fetchAnalyses();
    } else if (view === 'images' && selectedSample) {
      fetchImages(selectedSample, null, 'initial', false);
    } else if (view === 'snapshot_images' && selectedSnapshot) {
      fetchImages(selectedSnapshot, null, 'initial', true);
    }
  };

  const handleBack = () => {
    if (view === 'images') {
      setView('samples');
      setSelectedSample(null);
      props.onClickSample(null);
      fetchSamples(selectedExperiment);
    } else if (view === 'samples') {
      setView('experiments');
      setSelectedExperiment(null);
      props.onClickOpenExperiment(null);
      fetchExperiments();
    } else if (view === 'snapshot_images') {
      setView('snapshots');
      setSelectedSnapshot(null);
      props.onClickSnapshot?.(null);
      fetchSnapshots();
    }
  };

  const openExperiment = (raw) => {
    setSelectedExperiment(raw);
    setView('samples');
    setSearch('');
    props.onClickOpenExperiment(raw);
    fetchSamples(raw);
  };

  const openSample = (raw) => {
    setSelectedSample(raw);
    setView('images');
    setSearch('');
    props.onClickSample(raw);
    fetchImages(raw, null, 'initial', false);
  };

  const openSnapshot = (raw) => {
    setSelectedSnapshot(raw);
    setView('snapshot_images');
    setSearch('');
    props.onClickSnapshot?.(raw);
    fetchImages(raw, null, 'initial', true);
  };

  const openAnalysis = (analysisId) => {
    const meta = (data || []).find((d) => d.name === analysisId);
    const sp = (meta?.source_path || '').toLowerCase();
    const isSingle = /\.(jpg|jpeg|png|webp)$/.test(sp) || (meta?.frame_count != null && Number(meta.frame_count) <= 1);
    const modeSuffix = isSingle ? '&mode=single' : '';
    window.open(
      `/segmentation?analysisId=${encodeURIComponent(analysisId)}&deviceBucket=${encodeURIComponent(props.device.device_bucket)}${modeSuffix}`,
      '_blank',
    );
  };

  // FileItem-compatible click router
  const handleFileItemClick = (raw) => {
    const name = typeof raw === 'string' ? raw : raw?.name;
    if (!name) return;
    if (view === 'experiments') openExperiment(name);
    else if (view === 'samples') openSample(name);
    else if (view === 'snapshots') openSnapshot(name);
    else if (view === 'analyses') openAnalysis(name);
    // Image grids (sample images + snapshot images): clicking a thumbnail must
    // select it for the viewer. Without this, only the auto-selected frame
    // (fetchImages -> handleImageSelection of the last image) ever displays.
    else if (view === 'images' || view === 'snapshot_images') props.handleImageSelection?.(name);
  };

  // ---------- downloads / mp4 ----------

  const downloadImageFile = (fileName) => {
    const basePath = selectedSnapshot
      ? `${props.device.device_bucket}/Experiments/${selectedSnapshot}/${fileName}`
      : `${props.device.device_bucket}/Experiments/${selectedExperiment}/${selectedSample}/${fileName}`;
    s3.getObject({ Bucket: process.env.REACT_APP_USER_BUCKET, Key: basePath }, (err, payload) => {
      if (err) {
        console.error('Error downloading image:', err);
        setAlertMessage('Image download failed.');
        setAlertSeverity('error');
        setAlertOpen(true);
        return;
      }
      saveAs(new Blob([payload.Body], { type: payload.ContentType }), fileName);
    });
  };

  const downloadSampleFolder = async (sample) => {
    await ensureClients();
    const folderPath = `${props.device.device_bucket}/Experiments/${selectedExperiment}/${sample}`;
    try {
      const listed = await s3.listObjectsV2({
        Bucket: process.env.REACT_APP_USER_BUCKET,
        Prefix: folderPath,
      }).promise();
      if (!listed.Contents?.length) return;
      const zip = new JSZip();
      const total = listed.Contents.length;
      let done = 0;
      setIsDownloading(true);
      await Promise.all(listed.Contents.map(async (obj) => {
        const url = s3.getSignedUrl('getObject', {
          Bucket: process.env.REACT_APP_USER_BUCKET,
          Key: obj.Key,
        });
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch ${obj.Key}`);
        const blob = await res.blob();
        zip.file(obj.Key.replace(`${folderPath}/`, ''), blob);
        done += 1;
        setDownloadProgress((done / total) * 100);
      }));
      saveAs(await zip.generateAsync({ type: 'blob' }), `${sample}.zip`);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const createMp4 = async (sample) => {
    await ensureClients();
    const key = `${props.device.device_bucket}/Experiments/${selectedExperiment}/${sample}/movie.mp4`;
    try {
      setMovieDownloading(true);
      await s3.headObject({ Bucket: process.env.REACT_APP_USER_BUCKET, Key: key }).promise();
      const payload = await s3.getObject({ Bucket: process.env.REACT_APP_USER_BUCKET, Key: key }).promise();
      saveAs(new Blob([payload.Body], { type: 'video/mp4' }), 'movie.mp4');
    } catch (err) {
      console.error('Video not available:', err);
      setAlertMessage('Video not available for this sample.');
      setAlertSeverity('warning');
      setAlertOpen(true);
    } finally {
      setMovieDownloading(false);
    }
  };

  const handleDeleteAnalysis = async (analysisId) => {
    try {
      const { deleteAnalysis } = await import('../utils/apiClient');
      await deleteAnalysis(props.device.device_bucket, analysisId);
      delete cache.current[`analyses-${props.device.device_bucket}`];
      fetchAnalyses();
      setAlertMessage(`Analysis "${analysisId}" deleted.`);
      setAlertSeverity('success');
      setAlertOpen(true);
    } catch (err) {
      console.error('Failed to delete analysis:', err);
      setAlertMessage('Failed to delete analysis.');
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  // ---------- display data ----------

  // Apply client-side search to the active view's data.
  const visibleData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return (data || []).filter((item) => {
      if (typeof item === 'string') return item.toLowerCase().includes(q);
      if (item?.raw) return item.raw.toLowerCase().includes(q) || (item.title || '').toLowerCase().includes(q);
      if (item?.name) {
        return (item.analysis_name || '').toLowerCase().includes(q) || item.name.toLowerCase().includes(q);
      }
      return false;
    });
  }, [data, search]);

  // Reshape data into FileItem's uniform { raw, title, subtitle, meta } items.
  const fileItemLevel = (() => {
    switch (view) {
      case 'experiments': return 'experiments';
      case 'samples': return 'samples';
      case 'snapshots': return 'snapshots';
      case 'analyses': return 'analyses';
      case 'images':
      case 'snapshot_images': return 'images';
      default: return view;
    }
  })();

  const fileItemItems = useMemo(() => {
    if (view === 'experiments' || view === 'snapshots') {
      return visibleData.map((x) => ({ raw: x.raw, title: x.title, subtitle: x.subtitle }));
    }
    if (view === 'samples') {
      return visibleData.map((s) => {
        const f = formatSampleName(s);
        return { raw: s, title: f.title, subtitle: f.subtitle };
      });
    }
    if (view === 'analyses') {
      return visibleData.map((a) => ({
        raw: a.name,
        title: a.analysis_name || a.name,
        subtitle: [a.frame_count ? `${a.frame_count} frames` : null, a.object_count ? `${a.object_count} objects` : null]
          .filter(Boolean).join(' · ') || undefined,
        meta: a,
      }));
    }
    // images / snapshot_images
    return visibleData.map((filename) => {
      const f = formatImageName(filename);
      return { raw: filename, title: f.title, subtitle: f.subtitle };
    });
  }, [view, visibleData]);

  // Breadcrumb / header label
  const breadcrumb = (() => {
    if (view === 'experiments') return 'Experiments';
    if (view === 'snapshots') return 'Snapshots';
    if (view === 'analyses') return 'Saved Analyses';
    if (view === 'samples' && selectedExperiment) {
      const f = formatExperimentName(selectedExperiment);
      return f.title;
    }
    if (view === 'images' && selectedSample) {
      const { title } = formatSampleName(selectedSample);
      const exp = selectedExperiment ? formatExperimentName(selectedExperiment).title : '';
      return exp ? `${exp} / ${title}` : title;
    }
    if (view === 'snapshot_images' && selectedSnapshot) {
      return formatExperimentName(selectedSnapshot).title;
    }
    return '';
  })();

  const showBack = view === 'samples' || view === 'images' || view === 'snapshot_images';
  const showTabs = view === 'experiments' || view === 'snapshots' || view === 'analyses';

  const searchPlaceholder = (() => {
    switch (view) {
      case 'experiments': return 'Search experiments…';
      case 'samples': return 'Search samples…';
      case 'snapshots': return 'Search snapshots…';
      case 'analyses': return 'Search analyses…';
      case 'images':
      case 'snapshot_images': return 'Search images…';
      default: return 'Search…';
    }
  })();

  // ---------- render ----------

  return (
    <div>
      {/* Header: back + breadcrumb + refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {showBack && (
          <IconButton onClick={handleBack} size="small" sx={{ color: '#bbb' }} aria-label="Back">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {breadcrumb}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} size="small" sx={{ color: '#bbb' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab strip at top levels only */}
      {showTabs && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            mb: 1,
            '& .MuiTab-root': {
              color: '#aaa',
              fontSize: '0.85rem',
              textTransform: 'none',
              minHeight: 36,
              padding: '6px 10px',
            },
            '& .Mui-selected': { color: '#fff !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#7c3bed' },
          }}
        >
          <Tab value="experiments" label="Experiments" />
          <Tab value="snapshots" label="Snapshots" />
          <Tab value="analyses" label="Analyses" />
        </Tabs>
      )}

      {/* Search */}
      <TextField
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        fullWidth
        placeholder={searchPlaceholder}
        sx={{
          mb: 1,
          '& .MuiOutlinedInput-root': {
            color: '#fff',
            backgroundColor: '#1a1a1a',
            '& fieldset': { borderColor: '#333' },
            '&:hover fieldset': { borderColor: '#555' },
            '&.Mui-focused fieldset': { borderColor: '#7c3bed' },
          },
          '& input::placeholder': { color: '#888', opacity: 1 },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: '#888' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Compare button on Analyses tab */}
      {view === 'analyses' && data.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CompareArrowsIcon />}
            onClick={() => window.open(`/compare?deviceBucket=${encodeURIComponent(props.device.device_bucket)}`, '_blank')}
            sx={{
              color: '#7c3bed',
              borderColor: '#7c3bed',
              textTransform: 'none',
              '&:hover': { borderColor: '#6a2bc7', bgcolor: 'rgba(124, 59, 237, 0.08)' },
            }}
          >
            Compare Analyses
          </Button>
        </Box>
      )}

      {/* Image pagination (top) */}
      {(view === 'images' || view === 'snapshot_images') && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 1 }}>
          <Tooltip title="Previous page" arrow>
            <span>
              <IconButton
                disabled={prevTokens.length === 0}
                onClick={() => {
                  const prevToken = prevTokens.length >= 2 ? prevTokens[prevTokens.length - 2] : null;
                  fetchImages(selectedSample || selectedSnapshot, prevToken, 'prev', view === 'snapshot_images');
                }}
                sx={{
                  backgroundColor: prevTokens.length === 0 ? 'rgba(80,80,80,0.4)' : 'rgba(33,150,243,1)',
                  color: prevTokens.length === 0 ? 'rgba(120,120,120,0.6)' : '#fff',
                  width: 36, height: 36,
                  '&:hover': { backgroundColor: prevTokens.length === 0 ? 'rgba(80,80,80,0.4)' : 'rgba(25,118,210,1)' },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next page" arrow>
            <span>
              <IconButton
                disabled={!nextToken && forwardTokens.length === 0}
                onClick={() => {
                  const tokenToUse = nextToken || (forwardTokens.length > 0 ? forwardTokens[forwardTokens.length - 1] : null);
                  fetchImages(selectedSample || selectedSnapshot, tokenToUse, 'next', view === 'snapshot_images');
                }}
                sx={{
                  backgroundColor: (!nextToken && forwardTokens.length === 0) ? 'rgba(80,80,80,0.4)' : 'rgba(33,150,243,1)',
                  color: (!nextToken && forwardTokens.length === 0) ? 'rgba(120,120,120,0.6)' : '#fff',
                  width: 36, height: 36,
                  '&:hover': { backgroundColor: (!nextToken && forwardTokens.length === 0) ? 'rgba(80,80,80,0.4)' : 'rgba(25,118,210,1)' },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* List body */}
      {isLoading ? (
        <Box sx={{ px: 1 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={44}
              sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1, mb: 1 }}
            />
          ))}
        </Box>
      ) : visibleData.length === 0 ? (
        <Box sx={{ textAlign: 'center', color: '#888', py: 4 }}>
          <Typography variant="body2">
            {search ? 'No results match your search.' : 'Nothing here yet.'}
          </Typography>
        </Box>
      ) : (
        <div className="file-component-display">
          <FileList
            items={fileItemItems}
            level={fileItemLevel}
            onItemClick={(raw) => handleFileItemClick(raw)}
            onDeleteImage={(filename) => {
              // legacy: no delete-image endpoint; surface a non-destructive alert
              console.warn('Image delete not implemented for', filename);
            }}
            onDownloadImage={(filename) => {
              props.handleImageSelection?.(filename);
              downloadImageFile(filename);
            }}
            onAnalyzeImage={(filename) => {
              const basePath = selectedSnapshot
                ? `${props.device.device_bucket}/Experiments/${selectedSnapshot}`
                : `${props.device.device_bucket}/Experiments/${selectedExperiment}/${selectedSample}`;
              const fullPath = filename ? `${basePath}/${filename}` : basePath;
              window.open(`/segmentation?path=${encodeURIComponent(fullPath)}&mode=single`, '_blank');
            }}
            onDownloadSample={(sample) => downloadSampleFolder(sample)}
            onCreateMp4={(sample) => createMp4(sample)}
            onAnalyzeSample={(sample) => {
              const path = `${props.device.device_bucket}/Experiments/${selectedExperiment}/${sample}`;
              window.open(`/segmentation?path=${encodeURIComponent(path)}`, '_blank');
            }}
            onOpenAnalysis={(analysisId) => openAnalysis(analysisId)}
            onDeleteAnalysis={handleDeleteAnalysis}
          />
        </div>
      )}

      {isDownloading && <LinearProgress variant="determinate" value={downloadProgress} sx={{ mt: 1 }} />}
      {movieDownloading && <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 1 }} />}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={() => setAlertOpen(false)}>
        <Alert severity={alertSeverity}>{alertMessage}</Alert>
      </Snackbar>
    </div>
  );
}

export default FileExplorer;
